import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { KeenIcon } from '@/components/keenicons';
import { cn } from '@/lib/utils';
import { MergeFieldNode } from './nodes/MergeFieldNode';
import { FillInNode } from './nodes/FillInNode';
import { PageBreakNode } from './nodes/PageBreakNode';
import { fillInsIn } from './letterHtml';

const EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
  Underline,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Link.configure({ openOnClick: false, autolink: true }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  MergeFieldNode,
  FillInNode,
  PageBreakNode,
];

const Btn = ({ active, onClick, title, children, disabled }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={cn('editor-btn', { 'is-active': active, 'opacity-40 cursor-not-allowed': disabled })}
  >
    {children}
  </button>
);

/**
 * Rich-text editor for letter templates and demand letters. Adds tables and
 * the two chip nodes to the app's TipTap setup, and exposes insertToken /
 * insertFillIn / nextFillIn through the ref for the merge-field picker.
 */
const LetterEditor = forwardRef(({
  value,
  onChange,
  readOnly = false,
  mode = 'template',
  minHeight = 520,
  className,
}, ref) => {
  const [, setTick] = useState(0);

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor: e }) => onChange?.(e.getHTML()),
    onSelectionUpdate: () => setTick((t) => t + 1),
    onTransaction: () => setTick((t) => t + 1),
  });

  // Reload when the parent hands us a different body (load, regenerate) but
  // never while the drafter is typing — the parent stores exactly getHTML().
  useEffect(() => {
    if (!editor || value === undefined) return;
    if (value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [readOnly, editor]);

  const remainingFillIns = useMemo(() => fillInsIn(value || '').length, [value]);

  const insertToken = (token, label) => {
    if (!editor || readOnly) return;
    editor.chain().focus().insertContent([
      { type: 'mergeField', attrs: { token, label: label || token } },
      { type: 'text', text: ' ' },
    ]).run();
  };

  const insertFillIn = (label) => {
    if (!editor || readOnly) return;
    const text = label ?? window.prompt('What should the drafter write here? (e.g. Narrative of Facts)');
    if (!text) return;
    editor.chain().focus().insertContent([
      { type: 'fillIn', attrs: { label: text.trim() } },
      { type: 'text', text: ' ' },
    ]).run();
  };

  const nextFillIn = () => {
    if (!editor) return false;
    const from = editor.state.selection.from;
    let found = null;
    let first = null;
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== 'fillIn') return true;
      if (first === null) first = pos;
      if (found === null && pos > from) found = pos;
      return true;
    });
    const target = found ?? first;
    if (target === null) return false;
    editor.chain().focus().setNodeSelection(target).scrollIntoView().run();
    return true;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- the handlers close over editor/readOnly only
  useImperativeHandle(ref, () => ({ insertToken, insertFillIn, nextFillIn, editor }), [editor, readOnly]);

  if (!editor) return null;

  const inTable = editor.isActive('table');
  const headingLevel = [1, 2, 3, 4].find((level) => editor.isActive('heading', { level })) || 0;

  return (
    <div className={cn('rich-text-editor letter-editor input flex-col w-full shrink-0 px-0', className)}>
      {!readOnly && (
        <div className="toolbar flex flex-wrap items-center gap-1 p-1 border-b border-gray-200">
          <select
            className="select select-sm w-32"
            value={headingLevel}
            onChange={(e) => {
              const level = Number(e.target.value);
              if (level === 0) editor.chain().focus().setParagraph().run();
              else editor.chain().focus().toggleHeading({ level }).run();
            }}
          >
            <option value={0}>Paragraph</option>
            <option value={1}>Heading 1</option>
            <option value={2}>Heading 2</option>
            <option value={3}>Heading 3</option>
            <option value={4}>Heading 4</option>
          </select>

          <Btn title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><KeenIcon icon="text-bold" /></Btn>
          <Btn title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><KeenIcon icon="text-italic" /></Btn>
          <Btn title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><KeenIcon icon="text-underline" /></Btn>
          <Btn title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><KeenIcon icon="text-circle" /></Btn>
          <Btn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><KeenIcon icon="text-number" /></Btn>
          <Btn title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>Left</Btn>
          <Btn title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>Center</Btn>
          <Btn title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>Right</Btn>
          <Btn title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>Quote</Btn>
          <Btn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>Rule</Btn>
          <Btn title="Page break (PDF)" onClick={() => editor.chain().focus().setPageBreak().run()}>Page break</Btn>

          <span className="mx-1 text-gray-300">|</span>
          {!inTable ? (
            <Btn title="Insert a 2-column table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run()}>Table</Btn>
          ) : (
            <>
              <Btn title="Add row below" onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row</Btn>
              <Btn title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()}>− Row</Btn>
              <Btn title="Add column after" onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col</Btn>
              <Btn title="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()}>− Col</Btn>
              <Btn title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>Del table</Btn>
            </>
          )}

          <span className="mx-1 text-gray-300">|</span>
          {mode === 'template' && (
            <Btn title="Insert a prompt the drafter must replace" onClick={() => insertFillIn()}>+ Fill-in</Btn>
          )}
          {mode === 'letter' && (
            <Btn title="Jump to the next prompt to fill in" onClick={nextFillIn} disabled={remainingFillIns === 0}>
              Next fill-in {remainingFillIns > 0 ? `(${remainingFillIns})` : ''}
            </Btn>
          )}
          <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>Undo</Btn>
          <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>Redo</Btn>
        </div>
      )}

      <EditorContent
        editor={editor}
        className="editor-content w-full overflow-auto px-0"
        style={{ minHeight }}
      />
    </div>
  );
});

LetterEditor.displayName = 'LetterEditor';

export { LetterEditor };
